import { type DataChild, type DataNode, isDataNode } from "./types";

// Flattens a JsonDumper tree into plain values, for data whose shape is
// known: an indexed array becomes an array, an assoc array or an object a
// record. References and cuts are dropped: a panel that needs them renders
// the tree with DataTree instead.
export function plain(value: unknown): unknown {
  if (!isDataNode(value)) {
    return value;
  }

  return plainNode(value);
}

function plainNode(node: DataNode): unknown {
  switch (node.t) {
    case "scalar":
    case "string":
      return node.v;
    case "ref":
      return undefined;
    case "array":
      return "indexed" === node.kind
        ? node.children.map((child) => plainNode(child.node))
        : record(node.children);
    case "object":
    case "resource":
      return { ...record(node.children), __class: node.class };
  }
}

function record(children: DataChild[]) {
  const result: Record<string, unknown> = {};

  for (const child of children) {
    result[String(child.key ?? "")] = plainNode(child.node);
  }

  return result;
}

export function asRecord(value: unknown): Record<string, unknown> {
  const flat = plain(value);

  return "object" === typeof flat && null !== flat && !Array.isArray(flat)
    ? (flat as Record<string, unknown>)
    : {};
}

export function asList(value: unknown): unknown[] {
  const flat = plain(value);

  if (Array.isArray(flat)) {
    return flat;
  }

  return "object" === typeof flat && null !== flat ? Object.values(flat) : [];
}

export function asString(value: unknown): string {
  const flat = plain(value);

  return null === flat || undefined === flat ? "" : String(flat);
}

export function asNumber(value: unknown): number {
  const flat = plain(value);

  return "number" === typeof flat ? flat : Number(flat) || 0;
}

// Walks a JsonDumper tree by keys and returns the node found there, still a
// tree, for a panel that flattens the rest but renders one branch as is.
export function branch(
  value: unknown,
  ...path: Array<string | number>
): unknown {
  let node = value;

  for (const key of path) {
    if (!isDataNode(node) || !("children" in node)) {
      return undefined;
    }

    node = node.children.find(
      (child) => String(child.key ?? "") === String(key),
    )?.node;
  }

  return node;
}
